import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

interface Founder {
  _id: string;
  heading: string;
  name: string;
  designation: string;
  role: string;
  shortDescription?: string;
  longDescription?: string;
  thumbnailImage?: string;
  video?: string;
}

export default function FounderSection() {
  const [founders, setFounders] = useState<Founder[]>([]);
  const [editingFounder, setEditingFounder] = useState<Founder | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Fetch founders
  const fetchFounders = async () => {
    try {
      const res = await axios.get<{ status: string; data: Founder[] }>(
          `${process.env.REACT_APP_LOCAL_URL}/api/founder-details`
      );
      if (res.data.status === "success") {
        setFounders(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching founders:", error);
    }
  };

  useEffect(() => {
    fetchFounders();
  }, []);

  // Handle edit
  const handleEdit = (founder: Founder) => {
    setEditingFounder(founder);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
 
    try {
      await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/founder-details/${id}`);
      fetchFounders(); // refresh
    } catch (error) {
      console.error("Error deleting founder:", error);
    }
  };

  return (
    <div className="p-4">
      <h2 className="font-lato-regular">Section - 07 </h2>

      {founders.map((founder) => (
        <div key={founder._id} className="border p-4 mb-4 rounded-md bg-white">
          <h2 className="text-2xl font-semibold py-4">{founder.heading}</h2>

          {founder.thumbnailImage && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${founder.thumbnailImage}`}
              alt={founder.name}
              className="w-full h-[20rem] object-cover object-top my-4 rounded-md"
            />
          )}

          {founder.video && (
            <iframe
              width="35%"
              height="100%"
              src={founder.video}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          )}

          <h2 className="text-2xl font-semibold pt-4">{founder.name}</h2>

          <h2 className="text-xl font-semibold py-1">{founder.designation}</h2>

          <p className=" font-lato-regular py-2">{founder.shortDescription}</p>

          <div className="flex justify-start gap-3 mt-3">
            <button
              onClick={() => handleEdit(founder)}
              className="bg-blue-500 text-white px-2 py-1 rounded"
            >
              <Icon icon={FaRegEdit} />
            </button>
            <button
              onClick={() => handleDelete(founder._id)}
              className="text-red-500 border border-red-500 px-2 py-1 rounded"
            >
              <Icon icon={MdDelete} />
            </button>
          </div>
        </div>
      ))}

      {showForm && editingFounder && (
        <FounderFormModal
          founder={editingFounder}
          onClose={() => setShowForm(false)}
          fetchFounders={fetchFounders}
        />
      )}
    </div>
  );
}

// -------------------- Modal Form --------------------
interface FounderFormModalProps {
  founder: Founder;
  onClose: () => void;
  fetchFounders: () => void;
}

const FounderFormModal = ({
  founder,
  onClose,
  fetchFounders,
}: FounderFormModalProps) => {
  const [heading, setHeading] = useState(founder.heading);
  const [name, setName] = useState(founder.name);
  const [designation, setDesignation] = useState(founder.designation);
  const [role, setRole] = useState(founder.role);
  const [video, setVideo] = useState(founder.video || "");
  const [shortDescription, setShortDescription] = useState(
    founder.shortDescription || ""
  );
  const [longDescription, setLongDescription] = useState(
    founder.longDescription || ""
  );
  const [thumbnailImage, setThumbnailImage] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("heading", heading);
    formData.append("name", name);
    formData.append("designation", designation);
    formData.append("role", role);
    formData.append("video", video);
    if (role === "home") formData.append("shortDescription", shortDescription);
    if (role === "about") formData.append("longDescription", longDescription);
    if (thumbnailImage) formData.append("thumbnailImage", thumbnailImage);

    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/founder-details/${founder._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      fetchFounders();
      onClose();
    } catch (error) {
      console.error("Error updating founder:", error);
      alert("Failed to update. Check console.");
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-lg w-[500px] shadow-lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            placeholder="Heading"
            required
            className="w-full border p-2 rounded"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="w-full border p-2 rounded"
          />
          <input
            type="text"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            placeholder="Designation"
            required
            className="w-full border p-2 rounded"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full border p-2 rounded"
          >
            <option value="home">Home</option>
            <option value="about">About</option>
          </select>
          {role === "home" && (
            <textarea
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder="Short Description"
              className="w-full border p-2 rounded"
            />
          )}
          {role === "about" && (
            <textarea
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value)}
              placeholder="Long Description"
              className="w-full border p-2 rounded"
            />
          )}
          <label className="block font-lato-bold">Upload Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setThumbnailImage(e.target.files?.[0] || null)}
            className="w-full border p-2 rounded"
          />

          <label className="block font-lato-bold">Video URL (YouTube)</label>
          <input
            type="text"
            value={video || ""}
            onChange={(e) => setVideo(e.target.value)}
            placeholder="Enter video URL"
            className="w-full border p-2 rounded"
          />

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
